import {
  BrandRequest,
  BrandResponse,
  BrandsRequest,
  BrandsResponse,
  CategoriesRequest,
  CategoriesResponse,
  CategoryRequest,
  CategoryResponse,
  DepartmentsRequest,
  DepartmentsResponse,
} from '@italist/grpc-proto/services/inventory_pb';
import { SearchProductsRequest, SearchProductsResponse, Sort, SortMap } from '@italist/grpc-proto/services/search_pb';
import { SearchProduct } from '@italist/grpc-proto/entities/search_pb';
import { SearchResult } from 'models/search';
import { buildSearchResultProductURL, getTopLevelCategoryFromUrl } from './url';
import { getCategoryData } from '../../common/models/Category';
import Departments from '../../models/departments';
import { isEmptyGeneralCategory } from '../../helpers/metadataValues/productList/conditionsHelpers';
import { isHomeCategory } from '../../helpers/metadataValues/productList/categoryHelpers';

export const getRelatedProducts = async (countryCode, currencyCode, alternativeCurrencies, brandId, departmentId) => {
  const searchClient = (await import('../services/search')).default;
  const promise = new Promise<SearchProductsResponse>((resolve, reject) => {
    const request = new SearchProductsRequest();
    request.setOffset(0);
    request.setLimit(11);
    request.setBrandIdsList([brandId]);
    request.setDepartmentIdsList([departmentId]);
    request.setBillingCurrency(currencyCode);
    request.setAlternativeCurrenciesList(alternativeCurrencies);
    request.setCountryCode(countryCode);
    searchClient.searchProducts(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result;
};

export const getProducts = async (req, departments, limit = 60) => {
  const searchClient = (await import('../services/search')).default;
  const currency = req.session.currency_code;
  const alternativeCurrencies = [];
  if (req.session.approx_currency_code && req.session.approx_currency_code !== '') {
    alternativeCurrencies.push(req.session.approx_currency_code);
  }
  const countryCode = req.session.mkt_country_code;
  const {
    categories,
    brands,
    colors,
    seasons,
    sizes,
    price_lte: priceLte,
    price_gte: priceGte,
    skip,
    heel_heights: heelHeights,
    sleeve_lengths: sleeveLengths,
    toe_types: toeTypes,
    sort_field: sortField,
    q,
    on_sale: onSale,
  } = req.query;
  const { departmentName, cat_ids: catIds, brandId, storeId } = req.params;
  const catIdsParsed = catIds && catIds.split(',');
  const categoryIds = catIdsParsed || categories || [];

  let brandIds = brands || [];
  if (brandId) {
    brandIds = [brandId];
  } else if (!Array.isArray(brandIds)) {
    brandIds = [brandIds];
  }

  let storeIds = [];
  if (storeId) {
    storeIds = [storeId];
  }

  let depIds = [];
  if (categoryIds.length === 0 && departmentName) {
    depIds = [departments.find(d => d.internalName === departmentName).id];
    if (departmentName !== 'kids') {
      depIds.push('12');
    }
  }

  const promise = new Promise<SearchProductsResponse>((resolve, reject) => {
    const request = new SearchProductsRequest();
    const priceRange = new SearchProductsRequest.PriceRange();
    const { RELEVANCE, PRICE_LOW, PRICE_HIGH, NEWEST } = Sort;

    let sortEnum: SortMap[keyof SortMap] = RELEVANCE;
    switch (sortField) {
      case 'price_asc':
        sortEnum = PRICE_LOW;
        break;
      case 'price_desc':
        sortEnum = PRICE_HIGH;
        break;
      default:
        sortEnum = NEWEST;
        break;
    }

    request.setOffset(skip);
    request.setLimit(limit);
    request.setSort(sortEnum);

    if (priceLte && priceLte > 0) {
      priceRange.setLte(priceLte);
    }
    if (priceGte && priceGte > 0) {
      priceRange.setGte(priceGte);
    }

    if (onSale && onSale === '1') {
      request.setOnSale(true);
    }

    request.setCategoryIdsList(categoryIds);
    request.setSeasonsList(seasons || []);
    request.setBrandIdsList(brandIds);
    request.setDepartmentIdsList(depIds);
    request.setSizesList(sizes || []);
    request.setStoreIdsList(storeIds);
    request.setFreetext(q);
    request.setPriceRange(priceRange);
    request.setBillingCurrency(currency);
    request.setAlternativeCurrenciesList(alternativeCurrencies);
    request.setCountryCode(countryCode);
    request.setSleeveLengthList(sleeveLengths || []);
    request.setHeelHeightList(heelHeights || []);
    request.setToeTypeList(toeTypes || []);
    request.setColorList(colors || []);
    searchClient.searchProducts(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result;
};

export const getBrands = async brandIds => {
  const inventoryClient = (await import('../services/inventory')).default;
  const promise = new Promise<BrandsResponse>((resolve, reject) => {
    const request = new BrandsRequest();
    request.setBrandIdsList(brandIds);
    inventoryClient.getBrands(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result;
};

export const getCategories = async () => {
  const inventoryClient = (await import('../services/inventory')).default;

  const promise = new Promise<CategoriesResponse>((resolve, reject) => {
    const request = new CategoriesRequest();
    inventoryClient.getCategories(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result;
};

export const getDepartments = async () => {
  const inventoryClient = (await import('../services/inventory')).default;

  const promise = new Promise<DepartmentsResponse>((resolve, reject) => {
    const request = new DepartmentsRequest();
    inventoryClient.getDepartments(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result;
};

export const getCategory = async categoryId => {
  const inventoryClient = (await import('../services/inventory')).default;

  const promise = new Promise<CategoryResponse>((resolve, reject) => {
    const request = new CategoryRequest();
    request.setCategoryId(categoryId);
    inventoryClient.getCategory(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result.getCategory();
};

export const getBrand = async brandId => {
  const inventoryClient = (await import('../services/inventory')).default;

  const promise = new Promise<BrandResponse>((resolve, reject) => {
    const request = new BrandRequest();
    request.setBrandId(brandId);
    inventoryClient.getBrand(request, (err, response) => {
      if (err) {
        reject(err);
      }
      resolve(response);
    });
  });

  const result = await promise;
  return result;
};

export const composeFilterSettings = async (
  filterSettings,
  categories,
  catIds,
  brands,
  brandId,
  heelHeights,
  colors,
  seasons,
  sizes,
  priceLte,
  priceGte,
  sleeveLengths,
  storeId,
  toeTypes,
  skip,
  sortField,
  q,
) => {
  const categoryIds = catIds || categories;

  const selectedBrands = brands || [];

  let brandsWithName = [];
  if (filterSettings.brands.optionsList.length > 0) {
    try {
      brandsWithName = (await getBrands(filterSettings.brands.optionsList)).toObject().brandsList;
    } catch (e) {
      brandsWithName = [];
    }
  }

  return {
    brandId: brandId || [],
    brands: brandsWithName,
    categoryIds: filterSettings.category_ids.optionsList,
    colors: filterSettings.colors.optionsList,
    departmentIds: filterSettings.department_ids.optionsList,
    heelHeights: filterSettings.heel_heights.optionsList,
    limit: 60,
    query: q || '',
    seasons: filterSettings.seasons.optionsList,
    selectedBrands,
    selectedCategoryIds: categoryIds,
    selectedColors: colors || [],
    selectedHeelHeights: heelHeights || [],
    selectedPriceGte: priceGte ? parseInt(priceGte, 10) : 0,
    selectedPriceLte: priceLte ? parseInt(priceLte, 10) : 0,
    selectedSeasons: seasons || [],
    selectedSizes: sizes || [],
    selectedSleeveLengths: sleeveLengths || [],
    selectedToeTypes: toeTypes || [],
    sizes: filterSettings.sizes.optionsList,
    skip: skip || 0,
    sleeveLengths: filterSettings.sleeve_lengths.optionsList,
    sortField: sortField || 'insert_time',
    storeId: storeId || [],
    toeTypes: filterSettings.toe_types.optionsList,
  };
};

const isSale = (_urlQueryParams: Record<string, string | string[]>): boolean => {
  const { ...urlQueryParams } = _urlQueryParams;
  return urlQueryParams && urlQueryParams.on_sale === '1';
};

const getHeaderDescription = async (category, categoriesParsed, brand, departmentName): Promise<string> => {
  if (brand) {
    return brand.description;
  }
  if (category) {
    return category.description;
  }

  if (departmentName) {
    if (departmentName === 'kids') {
      return `Shop new designer fashion for kids: clothing, shoes, accessories, and gifts for <a href="/kids/baby-girls/348/">baby girls</a>, 
    <a href="/kids/baby-boys/361/">baby boys</a>, <a href="/kids/girls/385/">young girls</a>, 
    and <a href="/kids/boys/399/">young boys</a>. Browse by category or select your favorite brand. 
    The best luxury kids wear is here, from <a href="/brands/golden-goose/536/kids/">Golden Goose sneakers</a> to <a href="/brands/gucci/112/kids/">Gucci tees</a> and <a href="/brands/moncler/119/kids/">Moncler puffers</a>. Explore all the designer "mini-me" possibilities.`;
    }
    const categoryFromDep = categoriesParsed.find(c => c.name.toLowerCase() === departmentName.toLowerCase());
    const departmentCategory = await getCategory(categoryFromDep.id);

    return departmentCategory.toObject().description;
  }
  return '';
};

export const getHeaderTitle = (category, brandName, departmentName, urlQueryParams): string => {
  if (!departmentName) {
    return '';
  }
  const departmentUserFacingName = Departments.getName(departmentName);
  const saleInfix = isSale(urlQueryParams) ? ' Sale' : '';
  const filteredBrandDepartmentTitle = Departments.filterBrandDepartmentTitle(
    brandName || '',
    departmentUserFacingName,
    saleInfix,
  );

  if (isEmptyGeneralCategory(category)) {
    if (Departments.getHomeName() === category?.name) {
      if (brandName) {
        return `${brandName}${saleInfix} for ${Departments.getHomeName()}`;
      }
      if (saleInfix) {
        return `${saleInfix} for ${Departments.getHomeName()}`;
      }
      return `${Departments.getHomeName()}`;
    }
    if (Departments.getKidsSubDepartmentsNames().includes(category?.name)) {
      if (brandName) {
        return `${brandName}${saleInfix} for ${category.name}`;
      }
      return `${saleInfix || 'Fashion'} for ${category.name}`;
    }
    if (brandName) {
      return `${filteredBrandDepartmentTitle}`;
    }
    return `${saleInfix || 'Fashion'} for ${departmentUserFacingName}`;
  }
  if (departmentName === Departments.getKidsInternalName()) {
    const topLevelCategory = getTopLevelCategoryFromUrl(departmentName, category.url?.substr(6));
    const topLevelCategoryName = Departments.getName(topLevelCategory);
    if (Departments.getKidsSubDepartmentsNames().includes(topLevelCategoryName)) {
      if (brandName) {
        return `${brandName} ${category.name}${saleInfix} for ${topLevelCategoryName}`;
      }
      return `${category.name}${saleInfix} for ${topLevelCategoryName}`;
    }
  }
  if (isHomeCategory(departmentName, category)) {
    if (brandName) {
      return `${brandName} ${category.name}${saleInfix}`;
    }
    return `${category.name}${saleInfix}`;
  }
  if (brandName) {
    return `${brandName} ${category.name}${saleInfix} for ${departmentUserFacingName}`;
  }
  return `${category.name}${saleInfix} for ${departmentUserFacingName}`;
};

export const getHeaderInfo = async (
  req,
  categoriesParsed,
  brand,
  urlQueryParams: Record<string, string | string[]>,
): Promise<{ description?: string; title?: string }> => {
  const { departmentName, cat_ids: catIds, storeName, storeId } = req.params;
  const catIdsFromUrl = catIds && catIds.split(',');
  if (storeId) {
    return {
      description: '',
      title: storeName,
    };
  }

  const { categories: catIdsFromQuery } = req.query;
  const categoryIds = catIdsFromUrl || catIdsFromQuery;
  const { category } = getCategoryData(categoryIds, categoriesParsed);
  return {
    description: await getHeaderDescription(category, categoriesParsed, brand, departmentName),
    title: getHeaderTitle(category, brand?.name, departmentName, urlQueryParams),
  };
};

export const decorateSearchResult = (product: SearchProduct.AsObject, navigationDepartment: string): SearchResult => {
  return {
    ...product,
    url: buildSearchResultProductURL(product, navigationDepartment),
  };
};
